/**
 * Returns a new array with @target_len elements taken randomly from @arr
 * @param arr         Input array
 * @param target_len  Number of elements taken from @arr
 */
export const takeRandomFromArray = <T>(arr: T[], target_len: number): T[] => {
  if(target_len > arr.length) throw new Error("takeRandomFromArray, Target len too large");
  
  const cpy = [...arr];
  if(target_len === arr.length) return cpy;
  while(cpy.length > target_len) {
    cpy.splice(Math.floor(Math.random() * cpy.length), 1);
  }
  return cpy;
}

/**
 * This is a more efficient version of takeRandomFromArray that runs in O(k) time and O(k) additional space
 * rather than O(n)/O(n) that takeRandomFromArray takes.
 * 
 * Note: This mutates @arr by reshuffeling it!
 */
export const takeKofN = <T>(arr: T[], k: number): T[] => {
  if(k > arr.length) throw new Error("k has to be <= arr.length");

  for(let i = 0; i < k; i++) {
    const rand: number = i+Math.floor(Math.random() * (arr.length-i));
    const rand_el: T = arr[rand];
    arr[rand] = arr[i];
    arr[i]    = rand_el;
  }
  return arr.slice(0, k);
}

/**
 * Applies a random permutation to the elements in @arr and returns it in a new array.
 * Does not mutate @arr
 */
export const shuffleArray = <T>(arr: T[]): T[] => {
  const cpy = [...arr];
  let len: number = cpy.length, i: number;
  let tmp: T;
  while(len) {
    i        = Math.floor(Math.random() * len--);
    tmp      = cpy[len];
    cpy[len] = cpy[i];
    cpy[i]   = tmp;
  }
  return cpy;
}

/**
 * Rearrange the position of an item in an array.
 * 
 * @array input array. Will not be mutated
 * @old_idx index of item to be moved
 * @new_idx target index
 * @return new array with the items from @array with the item from @old_idx moved to @new_idx
 */
export const rearrangeArray = <T>(array: T[], old_idx: number, new_idx: number): T[] => {
  const copy: T[] = [...array];
  copy.splice(new_idx, 0, ...copy.splice(old_idx, 1));
  return copy;
}

/**
 * 
 * @return a new array that contains the intersection of @lhs and @rhs , i.e. all elements 
 *  that are in both arrays.
 */
export const intersection = <T>(lhs: T[], rhs: T[]): T[] => {
  return lhs.filter(val => rhs.includes(val));
}

/**
 * 
 * @return a new array that contains the elements of @lhs that are not in @rhs , i.e. lhs \ rhs
 */
export const complement = <T>(lhs: T[], rhs: T[]): T[] => {
  return lhs.filter(val => !rhs.includes(val));
}

/**
 * Awaits @delay ms. Useful for debugging.
 */
export const sleep = async (delay: number) => {
  return new Promise(resolve => setTimeout(resolve, delay));
}

export const quantile = (sorted_arr: number[], q: number) => {
  if(q > 1 || q < 0) throw new Error("Incorrect percentile value " + q);

  const pos  = (sorted_arr.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  
  if (sorted_arr[base + 1] !== undefined) {
      return sorted_arr[base] + rest * (sorted_arr[base + 1] - sorted_arr[base]);
  } else {
      return sorted_arr[base];
  }
};

export const sum = (A: number[]): number => {
  return A.reduce((res, val) => res+val, 0);
}